import {useState, useEffect, useRef, Fragment} from 'react'
import {useTranslation} from 'next-i18next'
import Button from '@material-ui/core/Button'
import AppIcon from 'src/components/elements/icons/AppIcon'
import AppTypography from 'src/components/elements/typography/AppTypography'
import DatePickerMenu from 'src/components/elements/dates/DatePickerMenu'
import {useCalendarContext} from 'src/context/CalendarProvider'
import {useAppContext} from 'src/context/AppProvider'
import {DatesStatusMap} from 'src/types/form'
import {SelectDateType} from 'src/types/utils'
import {PropertyType} from 'src/types/property'
import {Label} from 'src/components/forms/textField/AppTextField'
import {getDateDifferences} from 'src/utils/date'
import {useFormattedDate1} from 'src/hooks/date'
import Visible from 'src/components/helpers/Visible'

export default function UniquePropertyDate(props: {
  property: PropertyType
  selectedDates: SelectDateType
  datesStatusMap?: DatesStatusMap
  disableFlexibleDates?: boolean
  hidden?: boolean
  setSelectedDates: (dateRange: SelectDateType) => void
}) {
  const {
    hidden,
    selectedDates,
    disableFlexibleDates,
    datesStatusMap,
    setSelectedDates,
  } = props

  const {t} = useTranslation('common')
  const {openDateRangePicker, setOpenDateRangePicker} = useAppContext()
  const {visibleMonths, setVisibleMonths, nextMonth, previousMonth} =
    useCalendarContext()
  const ref = useRef<HTMLButtonElement | null>(null)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  useEffect(() => {
    //select first month when first render if we have dates
    if (selectedDates[0]) {
      setVisibleMonths([
        selectedDates[0].format('YYYY-MM'),
        selectedDates[0].add(1, 'month').format('YYYY-MM'),
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!ref.current) {
      return
    }

    if (hidden) {
      return
    }

    if (openDateRangePicker && !anchorEl) {
      ref.current?.scrollIntoView()
      ref.current.click()
    }
  }, [anchorEl, hidden, openDateRangePicker])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setOpenDateRangePicker(false)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setOpenDateRangePicker(false)
  }
  const handleSetSelectedDates = (values: SelectDateType) => {
    setOpenDateRangePicker(false)
    setSelectedDates(values)
  }

  const start = useFormattedDate1(selectedDates[0])
  const end = useFormattedDate1(selectedDates[1])

  if (hidden) {
    return null
  }

  return (
    <Fragment>
      <Label optional label={<DateLabel selectedDates={selectedDates} />} />

      <Button
        className="flex flex-row items-center justify-start border border-solid border-gray-200 rounded-full w-full"
        onClick={handleClick}
        ref={ref}
      >
        <AppIcon name="calendar_other" className="ml-1 mr-3" />
        <AppTypography variant="body" neutralColor={start && end ? 800 : 400}>
          {!start || !end ? t('enter_dates') : `${start} - ${end}`}
        </AppTypography>
        <AppIcon
          name="arrowDown"
          style={{transform: anchorEl ? 'rotate(180deg)' : ''}}
          className="ml-auto w-4 h-4 text-gray-500"
        />
      </Button>
      <DatePickerMenu
        visibleMonths={visibleMonths}
        nextMonth={nextMonth}
        previousMonth={previousMonth}
        datesStatusMap={datesStatusMap}
        anchorEl={anchorEl}
        selectedDates={selectedDates}
        disableFlexibleDates={disableFlexibleDates}
        setSelectedDates={handleSetSelectedDates}
        handleClose={handleClose}
        showPrice
      />
    </Fragment>
  )
}

function DateLabel(props: {selectedDates: SelectDateType}) {
  const {selectedDates} = props
  const {t} = useTranslation('common')

  const nights = getDateDifferences(selectedDates[0], selectedDates[1])

  return (
    <Fragment>
      <span>{t('dates')}</span>
      <Visible when={nights > 0}>
        <AppTypography className="inline-block" variant="smallCaption">
          &nbsp;({nights}&nbsp;{t('nights')})
        </AppTypography>
      </Visible>
    </Fragment>
  )
}
